import { useEffect, useState } from 'react'
import { track, EVENTS } from '@/lib/mixpanel'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { IconLoader } from '@tabler/icons-react'
import { getLanguagePath } from '../lib/utils'
import { handleCheckout } from '@/lib/checkout'
import { useTranslation } from 'react-i18next'
import { handlePostAuthTasks } from '@/lib/auth-utils'

export default function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // TODO: this useEffect is way too fat. Refactor it to use a hook use-auth-callback and several utility functions
  useEffect(() => {
    let mounted = true;

    const handleError = (error: any, type: string) => {
      if (!mounted) {
        return;
      }

      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      console.error('Auth callback error:', error);

      track(EVENTS.SIGN_IN_ERROR, {
        error: message,
        type
      });

      setStatus('error');
      setErrorMessage(message);
      setTimeout(() => navigate(getLanguagePath('sign-in')), 3000);
    };

    // Profile creation is now handled by auth-utils

    const handlePendingPurchase = async (user: any) => {
      const storedPlanId = localStorage.getItem('pendingPurchasePlanId');

      if (!storedPlanId || !mounted) {
        return false;
      }

      // Go directly to Stripe checkout for fresh login
      await handleCheckout({
        planId: storedPlanId,
        userId: user.id,
        navigate,
        setIsLoading: () => {}, // No loading state needed here
        skipConfirmation: true,
      });

      return true;
    };

    const handleAuthCallback = async () => {
      try {
        // Get session
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) {
          return;
        }

        if (error || !session) {
          handleError(error || new Error('No session found'), 'session_error');
          return;
        }

        const user = session.user;

        // Handle all post-auth tasks (profile creation, tracking, etc.)
        await handlePostAuthTasks(user, session, 'redirect');

        // Handle pending purchase if exists
        const hasPendingPurchase = await handlePendingPurchase(user);
        if (hasPendingPurchase) {
          return;
        }

        // No pending purchase, proceed with normal redirect
        if (mounted) {
          const redirectTo = localStorage.getItem('authRedirectTo') || '/';
          localStorage.removeItem('authRedirectTo');
          navigate(getLanguagePath(redirectTo), { replace: true });
        }

      } catch (error) {
        handleError(error, 'auth_callback_error');
      }
    };

    handleAuthCallback();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Authentication Error</h2>
          <p className="text-sm text-muted-foreground mb-4">{errorMessage}</p>
          <p className="text-sm text-muted-foreground">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <IconLoader className="h-8 w-8 animate-spin text-sky-500" />
        <p className="text-sm text-muted-foreground">{t('auth.completingSignIn')}</p>
      </div>
    </div>
  );
}